<script lang="ts" setup>
	import type { PolicyResponse, TableColumn, TableFilterOptions } from '@/types'
	import usePurchaseInfo from '~/composables/usePurchaseInfo'

	const {
		ui = { table: 'mt-16' },
		purchaseInfo,
		columns,
		// options,
		enableRefresh = true,
		// options: tableOptions
		...props
	} = defineProps<{
		purchaseInfo?: PlanInformationContext | null
		loading?: boolean
		columns?: TableColumn[]
		enableRefresh?: boolean
		records: RecordsFn<PolicyResponse>
		tableConfig?: TableFilterOptions
		ui?: {
			table?: string
		}
	}>()
	const { tableRecordColumn } = usePurchaseInfo({ planInformation: () => purchaseInfo ?? null })
	const { formatDate } = useCustomDate()
	const { internationalNoFormat } = useCurrency()
	const recordColumn = ref<TableColumn[]>([])
	const tableEl = useTemplateRef('table')
	const policySelected = defineModel<string[]>('selected')

	const refreshTable = () => tableEl.value?.refreshRequest()

	defineEmits<{
		(event: 'trigger', payload: unknown): void
	}>()
	defineExpose({
		pagination: tableEl,
		refreshPolicies: refreshTable
	})

	watch(
		() => [columns, purchaseInfo],
		() => {
			if (columns && columns.length > 0) {
				recordColumn.value = columns
			} else {
				recordColumn.value = tableRecordColumn.value as TableColumn[]
			}
		},
		{ immediate: true }
	)
</script>

<template>
	<div>
		<ui-data-table
			ref="table"
			v-bind="props"
			v-model:selected-items="policySelected"
			:columns="recordColumn"
			:records
			:loading
			:enable-refresh
			:options="tableConfig"
			:class="ui?.table"
		>
			<template #bodyCell="{ column, record, index }">
				<slot name="bodyCell" :column :record :index>
					<slot :name="column?.key" :record="record">
						<span v-if="column?.key === 'first_name'">{{
							record?.meta?.first_name ?? record?.first_name ?? 'N/A'
						}}</span>
						<span v-if="column?.key === 'last_name'">{{
							record?.meta?.last_name ?? record?.last_name ?? 'N/A'
						}}</span>
						<span v-if="column?.key === 'phone_number'">{{
							record?.meta?.phone_number ?? record?.phone_number ?? 'N/A'
						}}</span>
						<span v-if="column?.key === 'email'">{{ record?.meta?.email ?? record?.email ?? 'N/A' }}</span>
						<span v-if="column?.key === 'dependants'">
							{{ record?.meta?.no_of_dependant || record?.no_of_dependant || 0 }}
						</span>
						<span v-if="column?.key === 'gadget_model'">
							{{ record?.meta?.gadget_model ?? record?.gadget_model ?? '---' }}
						</span>
						<span v-if="column?.key === 'serial_number'">
							{{ record?.meta?.serial_number ?? record?.serial_number ?? '---' }}
						</span>
						<span v-if="column?.key === 'gadget_brand'">
							{{ (record?.meta?.gadget_brand as IdAndName)?.name ?? record?.meta?.gadget_brand ?? '---' }}
						</span>
						<span v-if="column?.key === 'gadget_type'">
							{{ (record?.meta?.gadget_type as IdAndName)?.name ?? record?.meta?.gadget_type ?? 'N/A' }}
						</span>
						<span v-if="column?.key === 'custodian'">
							{{ record?.first_name || record?.meta?.first_name }}
							{{ record?.last_name || record?.meta?.last_name }}
						</span>

						<span v-if="column?.key === 'policy_type' || column?.key === 'health_policy_type'">
							{{ record?.meta?.health_policy_type ?? 'individual' }}
						</span>
						<span v-if="column?.key === 'vehicle_type'">
							{{ (record?.meta?.vehicle_type as IdAndName)?.name ?? record?.meta?.vehicle_type ?? 'N/A' }}
						</span>
						<span v-if="column?.key === 'plate_number'">
							{{ record?.meta?.plate_number ?? 'N/A' }}
						</span>
						<span v-if="column?.key === 'model'">
							{{
								(record?.meta?.vehicle_model as IdAndName)?.name ?? record?.meta?.vehicle_model ?? 'N/A'
							}}
						</span>
						<span v-if="column?.key === 'manufacturer'">
							{{ (record?.meta?.vehicle_make as IdAndName)?.name ?? record?.meta?.vehicle_make ?? 'N/A' }}
						</span>
						<span v-if="column?.key === 'location_name'">
							{{ record?.meta?.location_name || record?.meta?.destination }}
						</span>
						<span v-if="column?.key === 'end_date'">
							{{ formatDate(record?.meta?.end_date ?? record?.meta?.arrival_date, 'DD/MM/YYYY') }}
						</span>
						<span v-if="column?.key === 'start_date'">
							{{ formatDate(record?.meta?.start_date ?? record?.meta?.departure_date, 'DD/MM/YYYY') }}
						</span>
						<span v-if="column?.key === 'value' || column?.key === 'gadget_value'">
							{{
								internationalNoFormat(
									record?.meta?.gadget_value ??
										record?.meta?.vehicle_value ??
										record?.meta?.car_value ??
										0,
									'₦'
								)
							}}
						</span>

						<my-cover-plan-status
							v-else-if="column?.key === 'status'"
							:pending="!record?.policy_number"
							:plan-details="record || null"
						/>

						<button
							v-if="column?.key === 'details'"
							type="button"
							aria-label="activate staff"
							aria-roledescription="view beneficiary plan details"
							class="text-sm font-medium"
							@click="$emit('trigger', record)"
						>
							<span class="font-medium text-primary-2"> Activate </span>
						</button>
					</slot>
				</slot>
			</template>
		</ui-data-table>
	</div>
</template>
